import type { FlowEdge } from "@hali-shares/deck-schema";
import type { ELK as Elk, ElkExtendedEdge, ElkNode } from "elkjs/lib/elk.bundled.js";

export type Box = { x: number; y: number; w: number; h: number };
export type EdgePath = { kind: "forward" | "loop"; d: string; label?: { text: string; x: number; y: number } };
export type FlowLayout = { width: number; height: number; nodes: Map<string, Box>; edges: EdgePath[] };

export type LayoutOptions = {
  nodeW: number;
  nodeH: number;
  /** Preferred space between columns; shrinks to minGap when the flow is wider than maxWidth. */
  gap: number;
  minGap: number;
  rowGap: number;
  maxWidth: number;
  /** Distance from the lowest node to the first loop lane. */
  loopDrop: number;
};

export const TOP_LEVEL: LayoutOptions = {
  nodeW: 200,
  nodeH: 112,
  gap: 96,
  minGap: 48,
  rowGap: 48,
  maxWidth: 1152,
  loopDrop: 64,
};
export const DETAIL: LayoutOptions = {
  nodeW: 220,
  nodeH: 96,
  gap: 112,
  minGap: 48,
  rowGap: 32,
  maxWidth: 1152,
  loopDrop: 44,
};

const ARROW = 5; // forward paths stop short so the arrowhead tip lands on the card edge
const LOOP_LANE_STEP = 40;
const LABEL_H = 16;
const LOOP_LABEL_GAP = 12;

// ELK is ~1.4 MB; load it only when a deck is about to be laid out.
let elk: Promise<Elk> | undefined;
function getElk(): Promise<Elk> {
  elk ??= import("elkjs/lib/elk.bundled.js").then(({ default: ELK }) => new ELK());
  return elk;
}

function labelWidth(text: string): number {
  return Math.ceil(text.length * 7.5) + 12; // 13px Geist, generous
}

async function runElk(nodes: { id: string }[], edges: FlowEdge[], o: LayoutOptions, gap: number): Promise<ElkNode> {
  return (await getElk()).layout({
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.padding": "[top=0,left=0,bottom=0,right=0]",
      "elk.layered.spacing.nodeNodeBetweenLayers": String(gap),
      "elk.spacing.nodeNode": String(o.rowGap),
      "elk.spacing.edgeLabel": "4",
      "elk.layered.nodePlacement.strategy": "BRANDES_KOEPF",
      "elk.layered.nodePlacement.bk.fixedAlignment": "BALANCED",
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      "elk.edgeLabels.placement": "CENTER",
    },
    children: nodes.map((n) => ({ id: n.id, width: o.nodeW, height: o.nodeH })),
    edges: edges.map((e, i): ElkExtendedEdge => ({
      id: `e${i}`,
      sources: [e.from],
      targets: [e.to],
      labels: e.label ? [{ text: e.label, width: labelWidth(e.label), height: LABEL_H }] : [],
    })),
  });
}

function shorten(points: { x: number; y: number }[], by: number) {
  const a = points[points.length - 2];
  const b = points[points.length - 1];
  if (!a || !b) return points;
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  return [...points.slice(0, -1), { x: b.x - ((b.x - a.x) / len) * by, y: b.y - ((b.y - a.y) / len) * by }];
}

function pathD(points: { x: number; y: number }[]): string {
  return points.map((p, i) => `${i ? "L" : "M"} ${Math.round(p.x * 10) / 10} ${Math.round(p.y * 10) / 10}`).join(" ");
}

/**
 * Lays a flow out left to right with ELK. Loop edges are kept out of ELK (they would reverse the
 * layering) and drawn by hand: down from the source, along a lane under the diagram, up into the target.
 */
export async function layoutFlow(
  nodes: { id: string }[],
  edges: FlowEdge[],
  o: LayoutOptions,
  loopLabel?: string,
): Promise<FlowLayout> {
  const forward = edges.filter((e) => e.kind !== "loop");
  const loops = edges.filter((e) => e.kind === "loop");

  let graph = await runElk(nodes, forward, o, o.gap);
  if ((graph.width ?? 0) > o.maxWidth) {
    const columns = new Set(graph.children?.map((c) => c.x)).size;
    if (columns > 1) {
      const fit = Math.floor((o.maxWidth - columns * o.nodeW) / (columns - 1));
      graph = await runElk(nodes, forward, o, Math.max(o.minGap, Math.min(o.gap, fit)));
    }
  }

  const boxes = new Map<string, Box>(
    (graph.children ?? []).map((c) => [
      c.id,
      { x: c.x ?? 0, y: c.y ?? 0, w: c.width ?? o.nodeW, h: c.height ?? o.nodeH },
    ]),
  );

  const paths: EdgePath[] = (graph.edges ?? []).flatMap((e) =>
    (e.sections ?? []).map((s): EdgePath => {
      const label = e.labels?.[0];
      return {
        kind: "forward",
        d: pathD(shorten([s.startPoint, ...(s.bendPoints ?? []), s.endPoint], ARROW)),
        label: label?.text
          ? { text: label.text, x: (label.x ?? 0) + (label.width ?? 0) / 2, y: label.y ?? 0 }
          : undefined,
      };
    }),
  );

  const width = graph.width ?? 0;
  let height = graph.height ?? 0;
  const bottom = Math.max(0, ...[...boxes.values()].map((b) => b.y + b.h));
  loops.forEach((e, i) => {
    const s = boxes.get(e.from);
    const t = boxes.get(e.to);
    if (!s || !t) return;
    const lane = bottom + o.loopDrop + i * LOOP_LANE_STEP;
    const sx = s.x + s.w / 2;
    const tx = t.x + t.w / 2;
    const text = e.label ?? (loops.length === 1 ? loopLabel : undefined);
    paths.push({
      kind: "loop",
      d: pathD(
        shorten(
          [
            { x: sx, y: s.y + s.h },
            { x: sx, y: lane },
            { x: tx, y: lane },
            { x: tx, y: t.y + t.h },
          ],
          ARROW - 1,
        ),
      ),
      label: text ? { text, x: (sx + tx) / 2, y: lane + LOOP_LABEL_GAP } : undefined,
    });
    height = Math.max(height, lane + (text ? LOOP_LABEL_GAP + LABEL_H : 4));
  });

  return { width, height, nodes: boxes, edges: paths };
}
